<template>
  <div class="assistant-chat">
    <div class="chat-scroll" ref="scroll">
      <div v-if="!messages.length" class="chat-empty">
        <p class="empty-title">你好，我是飞鸟智能助手</p>
        <p class="empty-sub">数据查询、异常排查、周报摘要都可以找我</p>
        <el-button v-if="contextTitle" size="small" round @click="prefillContext">分析「{{ contextTitle }}」页面</el-button>
      </div>
      <div v-for="(m, i) in messages" :key="i" :class="['chat-msg', m.role]">
        <div v-if="m.role === 'user'" class="bubble">{{ m.text }}</div>
        <div v-else class="assistant-body">
          <div v-for="(t, j) in m.tools" :key="j" class="tool-card">
            <div class="tool-head">
              <i :class="t.status === 'running' ? 'el-icon-loading' : (t.status === 'error' ? 'el-icon-warning-outline' : 'el-icon-s-grid')"></i>
              <span class="tool-name">{{ t.name }}</span>
              <span v-if="t.status === 'running'" class="tool-status">执行中…</span>
              <span v-else-if="t.status === 'error'" class="tool-status tool-error">{{ t.error || '执行失败' }}</span>
              <span v-else class="tool-status">{{ t.durationMs }}ms</span>
            </div>
            <div v-if="t.status === 'done' && t.renderHint === 'table' && tableRows(t).length" class="tool-table">
              <div v-if="t.args" class="table-bar">
                <el-button size="mini" type="text" icon="el-icon-download" :loading="t.exporting" @click="exportTable(t)">导出 Excel</el-button>
              </div>
              <el-table :data="tableRows(t)" size="mini" max-height="320" border>
                <el-table-column
                  v-for="col in tableCols(t)"
                  :key="col"
                  :prop="col"
                  :label="col"
                  :min-width="colWidth(col)"
                  show-overflow-tooltip
                ></el-table-column>
              </el-table>
              <div class="tool-meta">{{ tableMeta(t) }}</div>
            </div>
            <div v-else-if="t.status === 'done' && t.renderHint === 'artifact'" class="artifact-area">
              <div v-if="artifactOf(t)" :class="['artifact-card', artifactOf(t).status]">
                <i :class="artifactIcon(t)"></i>
                <div class="artifact-info">
                  <div class="artifact-title">{{ artifactOf(t).title }}</div>
                  <div class="artifact-sub">
                    <template v-if="artifactOf(t).status === 'pending'">正在生成…（完成后企业微信通知）</template>
                    <template v-else-if="artifactOf(t).status === 'done'">{{ artifactSize(t) }} · 保留 30 天</template>
                    <template v-else>生成失败：{{ artifactOf(t).error || '未知原因' }}，可重新发起</template>
                  </div>
                </div>
                <el-button v-if="artifactOf(t).status === 'done'" size="mini" type="primary" plain icon="el-icon-download" @click="downloadOne(t)">下载</el-button>
              </div>
              <div v-else class="artifact-note">{{ artifactNote(t) }}</div>
            </div>
            <pre v-else-if="t.status === 'done' && t.summary" class="tool-summary">{{ t.summary }}</pre>
          </div>
          <div v-if="m.html" class="md-body" v-html="m.html"></div>
          <div v-if="m.done" class="msg-foot">
            <span v-if="m.aborted" class="aborted-tag">已停止</span>
            <span v-if="m.tokens && m.tokens.input" class="token-tag">{{ m.tokens.input + m.tokens.output }} tokens</span>
            <el-button v-if="m.messageId" type="text" size="mini" class="save-skill-btn" @click="openSave(m)">存为技能</el-button>
            <span v-if="m.messageId" class="feedback-area">
              <i :class="m.feedback === 1 ? 'el-icon-thumb active' : 'el-icon-thumb'" title="有帮助" @click="sendFeedback(m, 1)"></i>
              <i :class="m.feedback === -1 ? 'el-icon-thumb rotate active' : 'el-icon-thumb rotate'" title="不准确" @click="sendFeedback(m, -1)"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="skill-bar">
      <el-popover ref="skillPopover" placement="top-start" width="340" trigger="click">
        <el-button slot="reference" size="mini" icon="el-icon-magic-stick">技能</el-button>
        <div v-loading="skillsLoading" class="skill-pop">
          <div v-for="g in skillGroups" :key="g.key" class="skill-group">
            <div class="skill-group-title">{{ g.label }}</div>
            <div v-for="s in g.items" :key="s._id" class="skill-item" @click="activateSkill(s)">
              <div class="skill-name">{{ s.name }}<span class="skill-usage">{{ s.usageCount }} 次</span></div>
              <div class="skill-desc">{{ s.description }}</div>
            </div>
            <div v-if="!g.items.length" class="skill-empty">暂无</div>
          </div>
        </div>
      </el-popover>
      <el-tag v-if="activeSkillName" closable size="small" type="success" @close="clearSkill">已激活：{{ activeSkillName }}</el-tag>
    </div>
    <div class="chat-input">
      <div v-if="slashMatches.length" class="slash-hint">回车激活「{{ slashMatches[0].name }}」（继续输入可筛选）</div>
      <el-input
        v-model="input"
        type="textarea"
        :rows="2"
        resize="none"
        placeholder="输入问题，回车发送（Shift+回车换行）"
        :disabled="sending"
        @keydown.enter.native="onEnter"
      ></el-input>
      <div class="input-actions">
        <el-button v-if="!sending" type="primary" size="small" :disabled="!input.trim()" @click="send">发送</el-button>
        <el-button v-else type="danger" size="small" plain @click="stop">停止</el-button>
      </div>
    </div>
    <el-dialog title="存为技能" :visible.sync="saveVisible" width="440px" append-to-body>
      <el-form label-width="90px" size="small">
        <el-form-item label="名称">
          <el-input v-model="saveForm.name" maxlength="30" show-word-limit placeholder="如：每周五的差评复盘"></el-input>
        </el-form-item>
        <el-form-item label="触发描述">
          <el-input v-model="saveForm.description" maxlength="100" placeholder="什么情况下用这个技能（给 AI 匹配用）"></el-input>
        </el-form-item>
        <el-form-item label="可见性">
          <el-radio-group v-model="saveForm.visibility">
            <el-radio label="private">仅自己</el-radio>
            <el-radio label="dept">部门共享</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="saveVisible = false">取消</el-button>
        <el-button size="small" type="primary" :disabled="!saveForm.name.trim()" @click="confirmSave">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { chatStream, fetchSessionMessages, fetchSkillList, saveSkill, feedback, createArtifactExport, fetchArtifactStatus, downloadArtifact } from '@/utils/assistant'
import { renderMarkdown } from '@/utils/markdown'

/**
 * 共享对话面板（全局抽屉与独立会话页复用）
 * 流式渲染：text-delta 增量渲染 markdown；工具结果（表格）由前端直渲染，数字不经模型转述
 */
export default {
  name: 'AssistantChat',
  props: {
    sessionId: { type: String, default: '' },
    contextTitle: { type: String, default: '' }
  },
  data () {
    return {
      messages: [],
      input: '',
      sending: false,
      abortController: null,
      internalSessionId: this.sessionId,
      skills: [],
      skillsLoading: false,
      activeSkillId: '',
      activeSkillName: '',
      saveVisible: false,
      saveForm: { messageId: '', name: '', description: '', visibility: 'private' }
    }
  },
  computed: {
    skillGroups () {
      const labels = { official: '官方技能', dept: '部门共享', private: '我的技能' }
      return ['official', 'dept', 'private'].map(key => ({
        key,
        label: labels[key],
        items: this.skills.filter(s => s.visibility === key)
      }))
    },
    slashMatches () {
      if (!this.input.startsWith('/')) return []
      const kw = this.input.slice(1)
      return this.skills.filter(s => s.name.includes(kw)).slice(0, 5)
    }
  },
  watch: {
    sessionId (val) {
      // 自己发送时 meta 事件回传 sessionId 会先更新 internalSessionId，prop 随之变化属于同源回传，
      // 不重载（否则会用历史快照覆盖正在流式的消息）
      if (val === this.internalSessionId) return
      this.internalSessionId = val
      this.loadHistory()
    }
  },
  created () {
    this.loadSkills()
    if (this.internalSessionId) {
      this.loadHistory()
    }
  },
  beforeDestroy () {
    this.clearPollTimers()
  },
  methods: {
    onEnter (e) {
      if (e.shiftKey) return
      e.preventDefault()
      if (this.sending) return
      // 「/技能名」快捷激活：唯一匹配时回车即激活
      if (this.slashMatches.length === 1) {
        this.activateSkill(this.slashMatches[0])
        this.input = ''
        return
      }
      this.send()
    },
    prefillContext () {
      this.input = `关于「${this.contextTitle}」页面，帮我分析一下当前的数据`
    },
    async send () {
      const text = this.input.trim()
      if (!text || this.sending) return
      this.input = ''
      const assistantMsg = { role: 'assistant', text: '', html: '', tools: [], done: false, aborted: false, tokens: null }
      this.messages.push({ role: 'user', text }, assistantMsg)
      this.sending = true
      this.abortController = new AbortController()
      this.scrollToBottom()
      try {
        await chatStream({
          sessionId: this.internalSessionId,
          text,
          skillId: this.activeSkillId || undefined,
          signal: this.abortController.signal,
          onEvent: (event, payload) => this.handleEvent(assistantMsg, event, payload)
        })
      } catch (err) {
        if (err && err.name === 'AbortError') {
          assistantMsg.aborted = true
        } else {
          this.$message.error(err.message || 'AI 服务暂时不可用')
        }
      } finally {
        assistantMsg.done = true
        this.sending = false
        this.abortController = null
        this.scrollToBottom()
        this.$emit('refresh-sessions')
      }
    },
    handleEvent (msg, event, payload) {
      switch (event) {
        case 'meta':
          this.internalSessionId = payload.sessionId
          this.$emit('session', payload.sessionId)
          break
        case 'text-delta':
          msg.text += payload.text
          msg.html = renderMarkdown(msg.text)
          this.scrollToBottom()
          break
        case 'tool-start':
          msg.tools.push({ name: payload.name, status: 'running', args: payload.args, data: null, renderHint: '', durationMs: 0, error: '', summary: '' })
          this.scrollToBottom()
          break
        case 'tool-result': {
          const target = [...msg.tools].reverse().find(t => t.name === payload.name && t.status === 'running')
          if (target) {
            target.status = payload.ok ? 'done' : 'error'
            target.data = payload.data
            target.renderHint = payload.renderHint
            target.durationMs = payload.durationMs
            target.error = payload.error || ''
            // 导出受理回执：初始化产物卡片（无 artifactId=守卫/澄清话术，走降级文本块）
            if (payload.renderHint === 'artifact' && payload.data && payload.data.artifactId) {
              this.$set(target, 'artifact', {
                artifactId: payload.data.artifactId,
                title: payload.data.title || '导出文件',
                status: payload.data.status || 'pending',
                error: '',
                filename: '',
                fileSize: 0
              })
              if (target.artifact.status === 'pending') {
                this.pollArtifact(target)
              }
            }
          }
          this.scrollToBottom()
          break
        }
        case 'done':
          msg.messageId = payload.messageId
          msg.tokens = payload.tokens
          if (payload.aborted) msg.aborted = true
          break
        case 'error':
          this.$message.error(payload.message || 'AI 服务执行异常')
          break
        default:
          break
      }
    },
    stop () {
      if (this.abortController) {
        this.abortController.abort()
      }
    },
    async loadSkills () {
      this.skillsLoading = true
      try {
        this.skills = await fetchSkillList()
      } catch (err) {
        // 技能加载失败不阻断对话
        this.skills = []
      } finally {
        this.skillsLoading = false
      }
    },
    activateSkill (s) {
      this.activeSkillId = s._id
      this.activeSkillName = s.name
      if (this.$refs.skillPopover) {
        this.$refs.skillPopover.doClose()
      }
      this.$message.success(`已激活技能「${s.name}」，发送消息即按该技能执行`)
    },
    clearSkill () {
      this.activeSkillId = ''
      this.activeSkillName = ''
    },
    async sendFeedback (m, rating) {
      // 重复点击同一评分视为取消意图，但服务端只存最终评分——这里直接忽略，避免 UI 与库不一致
      if (m.feedback === rating) return
      // 点踩时询问原因（选填）
      let reason = ''
      if (rating === -1) {
        try {
          const { value } = await this.$prompt('哪里不准确？（数字不对/口径不对/看不懂，选填）', '反馈', {
            confirmButtonText: '提交',
            cancelButtonText: '取消',
            inputPlaceholder: '选填'
          })
          reason = value || ''
        } catch (err) {
          return
        }
      }
      try {
        await feedback(m.messageId, rating, reason)
        m.feedback = rating
        this.$message.success('已收到反馈')
      } catch (err) {
        this.$message.error(err.message || '反馈失败')
      }
    },
    openSave (m) {
      this.saveForm = {
        messageId: m.messageId,
        name: (m.text || '').slice(0, 12),
        description: '',
        visibility: 'private'
      }
      this.saveVisible = true
    },
    async confirmSave () {
      try {
        const { data: res } = await saveSkill({
          sessionId: this.internalSessionId,
          messageId: this.saveForm.messageId,
          name: this.saveForm.name,
          description: this.saveForm.description,
          visibility: this.saveForm.visibility
        })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success(`技能「${res.data.name}」已保存`)
        this.saveVisible = false
        this.loadSkills()
      } catch (err) {
        this.$message.error(err.message || '保存失败')
      }
    },
    async loadHistory () {
      // 先停掉旧消息上的产物轮询定时器（切会话重建 messages 后孤儿定时器会继续轮询）
      this.clearPollTimers()
      this.messages = []
      if (!this.internalSessionId) return
      try {
        const docs = await fetchSessionMessages(this.internalSessionId)
        this.messages = (docs || []).map(d => {
          if (d.role === 'user') {
            return { role: 'user', text: d.content }
          }
          const msg = {
            role: 'assistant',
            text: d.content,
            html: renderMarkdown(d.content),
            tools: (d.toolCalls || []).map(t => {
              const tool = {
                name: t.name,
                status: t.status === 'error' ? 'error' : 'done',
                renderHint: t.renderHint,
                durationMs: t.durationMs,
                error: t.error || '',
                // 历史消息只有结果摘要（大表不整存），表格重渲染看当前会话
                summary: t.resultSummary || '',
                args: t.args || null,
                exporting: false,
                artifact: null
              }
              // 导出回执很小（<500 字符），resultSummary 即完整 JSON，可还原产物卡片
              if (t.renderHint === 'artifact' && t.resultSummary) {
                try {
                  const parsed = JSON.parse(t.resultSummary)
                  if (parsed && parsed.artifactId) {
                    tool.artifact = {
                      artifactId: parsed.artifactId,
                      title: parsed.title || '导出文件',
                      status: parsed.status || 'pending',
                      error: '',
                      filename: '',
                      fileSize: 0
                    }
                  }
                } catch (err) {
                  // 摘要被截断等异常，降级为文本块
                }
              }
              return tool
            }),
            done: true,
            aborted: false,
            tokens: d.tokens,
            messageId: d._id,
            feedback: (d.feedback && d.feedback.rating) || 0
          }
          // 历史产物卡片状态以服务端现值刷新（受理时快照恒为 pending）
          msg.tools.forEach(t => {
            if (t.artifact) this.pollArtifact(t, true)
          })
          return msg
        })
        this.scrollToBottom()
      } catch (err) {
        this.$message.error(err.message || '会话加载失败')
      }
    },
    tableRows (t) {
      if (!t.data) return []
      if (Array.isArray(t.data.list)) return t.data.list.slice(0, 50)
      if (Array.isArray(t.data)) return t.data.slice(0, 50)
      return []
    },
    tableCols (t) {
      const rows = this.tableRows(t)
      if (!rows.length || typeof rows[0] !== 'object') return []
      return Object.keys(rows[0]).slice(0, 8)
    },
    tableMeta (t) {
      if (!t.data || typeof t.data !== 'object') return ''
      const parts = []
      const labels = { totalAssigned: '名下门店', abnormalCount: '异常门店', threshold: '阈值', attentionCount: '需关注' }
      Object.keys(t.data).forEach(k => {
        if (k === 'list' || k === 'truncated' || typeof t.data[k] === 'object' || typeof t.data[k] === 'boolean') return
        parts.push(`${labels[k] || k}：${t.data[k]}`)
      })
      return parts.join(' · ')
    },
    colWidth (col) {
      return Math.min(220, Math.max(90, col.length * 16 + 40))
    },
    artifactOf (t) {
      return t.artifact && t.artifact.artifactId ? t.artifact : null
    },
    artifactNote (t) {
      if (t.data && t.data.note) return t.data.note
      try {
        const parsed = JSON.parse(t.summary || '')
        if (parsed && parsed.note) return parsed.note
      } catch (err) {
        // 摘要非完整 JSON 时按原文展示
      }
      return t.summary || ''
    },
    artifactIcon (t) {
      const s = this.artifactOf(t)
      if (!s) return 'el-icon-document'
      if (s.status === 'pending') return 'el-icon-loading'
      if (s.status === 'done') return 'el-icon-document'
      return 'el-icon-warning-outline'
    },
    artifactSize (t) {
      const s = this.artifactOf(t)
      return s && s.fileSize ? `${Math.max(1, Math.round(s.fileSize / 1024))} KB` : '—'
    },
    async downloadOne (t) {
      try {
        await downloadArtifact(t.artifact.artifactId, t.artifact.filename || `${t.artifact.title}.xlsx`)
      } catch (err) {
        this.$message.error(err.message || '下载失败')
        if (t.artifact) this.pollArtifact(t, true)
      }
    },
    async exportTable (t) {
      this.$set(t, 'exporting', true)
      try {
        const { data: res } = await createArtifactExport(t.name, t.args || {})
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg || '导出未受理')
          return
        }
        this.$message.success(`「${res.data.title}」已开始生成，完成后将通过企业微信通知您，也可在下载中心查看`)
      } catch (err) {
        this.$message.error(err.message || '导出失败')
      } finally {
        this.$set(t, 'exporting', false)
      }
    },
    pollArtifact (t, immediate) {
      if (!t.artifact || !t.artifact.artifactId) return
      const poll = async () => {
        if (!t.artifact) return
        try {
          const doc = await fetchArtifactStatus(t.artifact.artifactId)
          if (doc) {
            t.artifact.status = doc.status
            t.artifact.error = doc.error || ''
            t.artifact.filename = doc.filename || ''
            t.artifact.fileSize = doc.fileSize || 0
          } else {
            // 产物不存在/已过期（TTL 删除或归属不符）：停止轮询并明确提示
            t.artifact.status = 'failed'
            t.artifact.error = '产物不存在或已过期（保留 30 天）'
          }
        } catch (err) {
          // 网络抖动忽略，按节奏继续
        }
        if (t.artifact && t.artifact.status === 'pending') {
          t.artifact._pollCount = (t.artifact._pollCount || 0) + 1
          if (t.artifact._pollCount <= 40) {
            t._pollTimer = setTimeout(poll, 15000)
          }
        }
      }
      if (immediate) poll()
      else t._pollTimer = setTimeout(poll, 15000)
    },
    clearPollTimers () {
      this.messages.forEach(m => {
        (m.tools || []).forEach(t => {
          if (t._pollTimer) {
            clearTimeout(t._pollTimer)
            t._pollTimer = null
          }
        })
      })
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.scroll
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.assistant-chat {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.chat-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f7f8fa;
}
.chat-empty {
  text-align: center;
  color: #909399;
  padding: 48px 0;
}
.empty-title {
  font-size: 15px;
  color: #606266;
  margin-bottom: 8px;
}
.empty-sub {
  font-size: 13px;
  margin-bottom: 16px;
}
.chat-msg {
  margin-bottom: 16px;
  display: flex;
}
.chat-msg.user {
  justify-content: flex-end;
}
.bubble {
  background: #409eff;
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  max-width: 78%;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
.assistant-body {
  max-width: 92%;
}
.tool-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  font-size: 12px;
}
.tool-head {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #606266;
}
.tool-name {
  font-weight: 600;
}
.tool-status {
  color: #909399;
}
.tool-error {
  color: #f56c6c;
}
.tool-meta {
  color: #909399;
  margin-top: 6px;
}
.table-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 2px;
}
.artifact-area {
  margin-top: 6px;
}
.artifact-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafbfc;
}
.artifact-card > i {
  font-size: 20px;
  color: #409eff;
}
.artifact-card.failed > i {
  color: #f56c6c;
}
.artifact-info {
  flex: 1;
  min-width: 0;
}
.artifact-title {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.artifact-sub {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}
.artifact-note {
  background: #f7f8fa;
  padding: 6px 8px;
  border-radius: 4px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}
.tool-summary {
  background: #f7f8fa;
  padding: 6px 8px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 160px;
  overflow-y: auto;
  margin: 6px 0 0;
  font-family: inherit;
}
.md-body {
  background: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.7;
  word-break: break-word;
}
.msg-foot {
  margin-top: 4px;
  font-size: 11px;
  color: #c0c4cc;
}
.aborted-tag {
  color: #e6a23c;
  margin-right: 8px;
}
.chat-input {
  border-top: 1px solid #ebeef5;
  padding: 10px 12px;
  background: #fff;
}
.skill-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}
.skill-pop {
  max-height: 360px;
  overflow-y: auto;
}
.skill-group {
  margin-bottom: 10px;
}
.skill-group-title {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}
.skill-item {
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
}
.skill-item:hover {
  background: #f7f8fa;
}
.skill-name {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
}
.skill-usage {
  font-size: 11px;
  color: #c0c4cc;
  margin-left: 6px;
  font-weight: 400;
}
.skill-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}
.skill-empty {
  font-size: 12px;
  color: #c0c4cc;
  padding: 4px 8px;
}
.slash-hint {
  font-size: 12px;
  color: #67c23a;
  margin-bottom: 4px;
}
.save-skill-btn {
  padding: 0;
  margin-left: 8px;
}
.feedback-area {
  margin-left: 8px;
}
.feedback-area i {
  cursor: pointer;
  color: #c0c4cc;
  margin-right: 6px;
}
.feedback-area i:hover,
.feedback-area i.active {
  color: #409eff;
}
.feedback-area i.rotate {
  transform: rotate(180deg);
  display: inline-block;
}
.input-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}
</style>

<style>
/* markdown 渲染（模型输出表格/加粗），非 scoped 以命中 v-html 内容 */
.md-body table {
  border-collapse: collapse;
  margin: 8px 0;
  width: 100%;
}
.md-body th,
.md-body td {
  border: 1px solid #ebeef5;
  padding: 4px 8px;
  font-size: 12px;
  text-align: left;
}
.md-body th {
  background: #f7f8fa;
}
.md-body p {
  margin: 6px 0;
}
.md-body ul,
.md-body ol {
  padding-left: 18px;
  margin: 6px 0;
}
</style>
